// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import type { ProductImageBatch } from "@/api/hooks/use-product-images";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// The backend keeps at most this many failures per batch; shown in the popover hint.
const MAX_ERRORS = 100;

// Same vocabulary as the shared ProgressBar: done on the primary colour, the muted track for the rest,
// and the destructive colour only for failures.
const DONE = "bg-primary";
const FAILED = "bg-destructive";

/**
 * Progress of one batch through the pipeline: the files that reached the end on the primary colour,
 * failed on the destructive one, the muted track for whatever is still in flight. Hovering the bar
 * shows how far each stage has got, as a share of the mapped files.
 */
export function BatchProgress({ batch }: { batch: ProductImageBatch }) {
  const { t } = useTranslation("back-office");
  const { mapped, received, uploaded, queued, processed, failed, queueFailed, processFailed } = batch.summary;
  // `failed` is every failure before the resize; the part Kafka rejected belongs to the queue stage
  const uploadFailed = failed - queueFailed;
  const failedTotal = failed + processFailed;
  const done = processed + failedTotal;
  const share = (count: number) => (mapped > 0 ? (Math.max(0, count) / mapped) * 100 : 0);
  const percent = mapped > 0 ? Math.round(share(done)) : 100;
  const stages = [
    { label: t("productImages.summary.received"), count: received, failed: 0 },
    { label: t("productImages.summary.uploaded"), count: uploaded, failed: uploadFailed },
    { label: t("productImages.summary.queued"), count: queued, failed: queueFailed },
    { label: t("productImages.summary.processed"), count: processed, failed: processFailed },
  ];

  return (
    <div className="flex min-w-[16rem] flex-col gap-1">
      <div className="flex items-center gap-2 text-xs leading-none tabular-nums">
        <span className={cn("font-medium", failedTotal > 0 ? "text-destructive" : "text-foreground")}>{percent}%</span>
        <span className="text-muted-foreground">{t("productImages.list.progressDone", { done, mapped })}</span>
        {failedTotal > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="ml-auto text-destructive underline-offset-2 hover:underline">
                {t("productImages.list.failed", { count: failedTotal })}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[32rem] p-0">
              <div className="border-b px-3 py-2">
                <p className="text-sm font-medium">{t("productImages.list.errorsTitle")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("productImages.list.errorsHint", { max: MAX_ERRORS })}
                </p>
              </div>
              <div className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("productImages.columns.fileName")}</TableHead>
                      <TableHead className="w-28">{t("productImages.columns.validation")}</TableHead>
                      <TableHead>{t("productImages.columns.detail")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batch.errors.map((error) => (
                      <TableRow key={`${error.stage}:${error.fileName}`}>
                        <TableCell className="font-medium">{error.fileName}</TableCell>
                        <TableCell>{t(`productImages.list.errorStage.${error.stage}`, error.stage)}</TableCell>
                        <TableCell className="whitespace-normal text-muted-foreground">{error.detail}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            className="flex h-2 w-full cursor-help overflow-hidden rounded-full bg-muted"
          >
            <div className={cn("h-full", DONE)} style={{ width: `${share(processed)}%` }} />
            <div className={cn("h-full", FAILED)} style={{ width: `${share(failedTotal)}%` }} />
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="space-y-1 tabular-nums">
          {stages.map((stage) => (
            <p key={stage.label}>
              {stage.label}: {stage.count}/{mapped} ({Math.round(share(stage.count))}%)
              {stage.failed > 0 && (
                <span className="ml-1 text-destructive">{t("productImages.list.failed", { count: stage.failed })}</span>
              )}
            </p>
          ))}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
